import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Badge,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Gavel,
  Warning,
  CheckCircle,
  Cancel,
  Person,
  Email,
  AttachMoney,
  CalendarToday,
  Description,
  PhotoCamera,
  Message,
  Description as DocumentIcon,
  Assignment,
  Timeline as TimelineIcon,
  ExpandMore,
  Visibility,
  Edit,
  Send,
  Close,
  Flag,
  Security,
  Assessment,
  Support,
  FilterList,
  Search,
  Refresh,
  Star,
  Euro,
  LocalShipping,
  VerifiedUser,
  Block
} from '@mui/icons-material';
import VerifiedBadge from './VerifiedBadge';

interface Dispute {
  id: string;
  type: 'damage' | 'non_return' | 'payment' | 'communication' | 'quality' | 'other';
  status: 'open' | 'investigating' | 'resolved' | 'closed' | 'escalated';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  complainant: {
    id: string;
    name: string;
    email: string;
    avatar: string;
    phone: string;
    isVerified: boolean;
  };
  respondent: {
    id: string;
    name: string;
    email: string;
    avatar: string;
    phone: string;
    isVerified: boolean;
  };
  bookingId: string;
  dressTitle: string;
  dressImage: string;
  amount: number;
  description: string;
  evidence: Array<{
    id: string;
    type: 'photo' | 'message' | 'document' | 'video';
    url: string;
    description: string;
    uploadedBy: string;
    timestamp: Date;
  }>;
  createdAt: Date;
  updatedAt: Date;
  assignedTo?: {
    id: string;
    name: string;
    email: string;
  };
  resolution?: {
    decision: 'favor_complainant' | 'favor_respondent' | 'partial_refund' | 'no_action';
    amount: number;
    description: string;
    resolvedBy: string;
    resolvedAt: Date;
  };
  timeline: Array<{
    id: string;
    action: string;
    description: string;
    timestamp: Date;
    user: string;
    type: 'status_change' | 'evidence_added' | 'comment' | 'resolution';
  }>;
  category: string;
  tags: string[];
}

interface DisputeManagementProps {
  onDisputeAction: (disputeId: string, action: string, data?: any) => void;
}

const DisputeManagement: React.FC<DisputeManagementProps> = ({ onDisputeAction }) => {
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDispute, setSelectedDispute] = useState<Dispute | null>(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openResolutionDialog, setOpenResolutionDialog] = useState(false);
  const [filter, setFilter] = useState<'all' | 'open' | 'investigating' | 'resolved' | 'urgent'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [resolutionData, setResolutionData] = useState({
    decision: '',
    amount: 0,
    description: ''
  });

  useEffect(() => {
    loadDisputes();
  }, []);

  const loadDisputes = async () => {
    setLoading(true);
    
    // Simulation du chargement des litiges
    setTimeout(() => {
      setDisputes([
        {
          id: '1',
          type: 'damage',
          status: 'open',
          priority: 'high',
          complainant: {
            id: '1',
            name: 'Marie Dubois',
            email: 'marie.dubois@email.com',
            avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150',
            phone: '+33 1 23 45 67 89',
            isVerified: true
          },
          respondent: {
            id: '2',
            name: 'Jean Martin',
            email: 'jean.martin@email.com',
            avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150',
            phone: '+33 1 98 76 54 32',
            isVerified: false
          },
          bookingId: 'BOOK_123',
          dressTitle: 'Robe de soirée élégante',
          dressImage: 'https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=150',
          amount: 150,
          description: 'La robe a été rendue avec une tache importante sur le devant. La tache semble être du vin rouge et n\'était pas présente lors de la réception.',
          evidence: [
            {
              id: '1',
              type: 'photo',
              url: '/evidence/damage_photo.jpg',
              description: 'Photo de la tache sur la robe',
              uploadedBy: 'Marie Dubois',
              timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
            },
            {
              id: '2',
              type: 'message',
              url: '/evidence/messages.txt',
              description: 'Échanges de messages entre les parties',
              uploadedBy: 'Marie Dubois',
              timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)
            }
          ],
          createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
          updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
          timeline: [
            {
              id: '1',
              action: 'Litige créé',
              description: 'Marie Dubois a créé un litige pour dommage',
              timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
              user: 'Marie Dubois',
              type: 'status_change'
            },
            {
              id: '2',
              action: 'Preuve ajoutée',
              description: 'Photo de la tache ajoutée',
              timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
              user: 'Marie Dubois',
              type: 'evidence_added'
            },
            {
              id: '3',
              action: 'Commentaire',
              description: 'Jean Martin conteste la responsabilité',
              timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
              user: 'Jean Martin',
              type: 'comment'
            }
          ],
          category: 'Dommage matériel',
          tags: ['tache', 'vin', 'nettoyage']
        },
        {
          id: '2',
          type: 'non_return',
          status: 'investigating',
          priority: 'urgent',
          complainant: {
            id: '3',
            name: 'Sophie Laurent',
            email: 'sophie.laurent@email.com',
            avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150',
            phone: '+33 1 55 44 33 22',
            isVerified: false
          },
          respondent: {
            id: '4',
            name: 'Pierre Durand',
            email: 'pierre.durand@email.com',
            avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150',
            phone: '+33 1 77 88 99 00',
            isVerified: true
          },
          bookingId: 'BOOK_124',
          dressTitle: 'Robe de cocktail moderne',
          dressImage: 'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=150',
          amount: 200,
          description: 'La robe n\'a pas été retournée dans les délais convenus. Le locataire ne répond plus aux messages.',
          evidence: [
            {
              id: '3',
              type: 'message',
              url: '/evidence/messages.txt',
              description: 'Échanges de messages montrant l\'absence de réponse',
              uploadedBy: 'Sophie Laurent',
              timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)
            }
          ],
          createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
          updatedAt: new Date(Date.now() - 2 * 60 * 60 * 1000),
          assignedTo: {
            id: 'admin_1',
            name: 'Admin Support',
            email: 'admin@allure.com'
          },
          timeline: [
            {
              id: '4',
              action: 'Litige créé',
              description: 'Sophie Laurent a créé un litige pour non-retour',
              timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
              user: 'Sophie Laurent',
              type: 'status_change'
            },
            {
              id: '5',
              action: 'Assigné',
              description: 'Litige assigné à Admin Support',
              timestamp: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000),
              user: 'Admin Support',
              type: 'status_change'
            },
            {
              id: '6',
              action: 'Enquête',
              description: 'Enquête en cours pour localiser le locataire',
              timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
              user: 'Admin Support',
              type: 'comment'
            }
          ],
          category: 'Non-retour',
          tags: ['disparition', 'non-reponse', 'urgence']
        },
        {
          id: '3',
          type: 'payment',
          status: 'resolved',
          priority: 'medium',
          complainant: {
            id: '5',
            name: 'Claire Moreau',
            email: 'claire.moreau@email.com',
            avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150',
            phone: '+33 1 11 22 33 44',
            isVerified: true
          },
          respondent: {
            id: '6',
            name: 'Antoine Petit',
            email: 'antoine.petit@email.com',
            avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150',
            phone: '+33 1 99 88 77 66',
            isVerified: true
          },
          bookingId: 'BOOK_125',
          dressTitle: 'Robe de mariée vintage',
          dressImage: 'https://images.unsplash.com/photo-1566479179817-c0d9ed0f5d55?w=150',
          amount: 500,
          description: 'Problème de paiement - transaction échouée mais robe livrée',
          evidence: [
            {
              id: '4',
              type: 'document',
              url: '/evidence/payment_log.pdf',
              description: 'Logs de paiement montrant l\'échec de la transaction',
              uploadedBy: 'Claire Moreau',
              timestamp: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000)
            }
          ],
          createdAt: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000),
          updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
          assignedTo: {
            id: 'admin_2',
            name: 'Admin Finance',
            email: 'finance@allure.com'
          },
          resolution: {
            decision: 'favor_complainant',
            amount: 500,
            description: 'Remboursement intégral accordé - problème technique confirmé',
            resolvedBy: 'Admin Finance',
            resolvedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)
          },
          timeline: [
            {
              id: '7',
              action: 'Litige créé',
              description: 'Claire Moreau a créé un litige pour problème de paiement',
              timestamp: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000),
              user: 'Claire Moreau',
              type: 'status_change'
            },
            {
              id: '8',
              action: 'Assigné',
              description: 'Litige assigné à Admin Finance',
              timestamp: new Date(Date.now() - 11 * 24 * 60 * 60 * 1000),
              user: 'Admin Finance',
              type: 'status_change'
            },
            {
              id: '9',
              action: 'Résolu',
              description: 'Remboursement intégral accordé',
              timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
              user: 'Admin Finance',
              type: 'resolution'
            }
          ],
          category: 'Problème de paiement',
          tags: ['paiement', 'technique', 'remboursement']
        }
      ]);
      setLoading(false);
    }, 1500);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'error';
      case 'investigating': return 'warning';
      case 'resolved': return 'success';
      case 'closed': return 'default';
      case 'escalated': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'damage': return <PhotoCamera />;
      case 'non_return': return <LocalShipping />;
      case 'payment': return <Euro />;
      case 'communication': return <Message />;
      case 'quality': return <Star />;
      default: return <Description />;
    }
  };

  const handleDisputeAction = (disputeId: string, action: string, data?: any) => {
    setDisputes(prevDisputes => 
      prevDisputes.map(dispute => {
        if (dispute.id === disputeId) {
          switch (action) {
            case 'assign':
              return { 
                ...dispute, 
                status: 'investigating' as const, 
                assignedTo: { id: 'admin_1', name: 'Admin Support', email: 'admin@allure.com' },
                timeline: [...dispute.timeline, {
                  id: Date.now().toString(),
                  action: 'Assigné',
                  description: 'Litige assigné à Admin Support',
                  timestamp: new Date(),
                  user: 'Admin Support',
                  type: 'status_change' as const
                }]
              };
            case 'resolve':
              return { 
                ...dispute, 
                status: 'resolved' as const,
                resolution: {
                  decision: data.decision,
                  amount: data.amount,
                  description: data.description,
                  resolvedBy: 'Admin Support',
                  resolvedAt: new Date()
                },
                timeline: [...dispute.timeline, {
                  id: Date.now().toString(),
                  action: 'Résolu',
                  description: data.description,
                  timestamp: new Date(),
                  user: 'Admin Support',
                  type: 'resolution' as const
                }]
              };
            case 'close':
              return { ...dispute, status: 'closed' as const };
            case 'escalate':
              return { ...dispute, status: 'escalated' as const, priority: 'urgent' as const };
            default:
              return dispute;
          }
        }
        return dispute;
      })
    );
    onDisputeAction(disputeId, action, data);
  };

  const filteredDisputes = disputes.filter(dispute => {
    const matchesFilter = filter === 'all' || 
      (filter === 'open' && dispute.status === 'open') ||
      (filter === 'investigating' && dispute.status === 'investigating') ||
      (filter === 'resolved' && dispute.status === 'resolved') ||
      (filter === 'urgent' && dispute.priority === 'urgent');
    
    const matchesSearch = searchTerm === '' || 
      dispute.dressTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dispute.complainant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dispute.respondent.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  const getResolutionDecisionText = (decision: string) => {
    switch (decision) {
      case 'favor_complainant': return 'En faveur du plaignant';
      case 'favor_respondent': return 'En faveur du défendeur';
      case 'partial_refund': return 'Remboursement partiel';
      case 'no_action': return 'Aucune action';
      default: return decision;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ color: '#a5816d', mb: 3 }}>
        <Gavel sx={{ mr: 1, verticalAlign: 'middle' }} />
        Gestion des litiges
      </Typography>

      {/* Filtres et recherche */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          placeholder="Rechercher un litige..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
          }}
          sx={{ minWidth: 300 }}
        />
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Filtrer</InputLabel>
          <Select
            value={filter}
            label="Filtrer"
            onChange={(e) => setFilter(e.target.value as any)}
          >
            <MenuItem value="all">Tous les litiges</MenuItem>
            <MenuItem value="open">Ouverts</MenuItem>
            <MenuItem value="investigating">En cours</MenuItem>
            <MenuItem value="resolved">Résolus</MenuItem>
            <MenuItem value="urgent">Urgents</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadDisputes}
        >
          Actualiser
        </Button>
      </Box>

      {/* Statistiques rapides */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error">
                {disputes.filter(d => d.status === 'open').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Litiges ouverts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning">
                {disputes.filter(d => d.status === 'investigating').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                En cours
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success">
                {disputes.filter(d => d.status === 'resolved').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Résolus
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error">
                {disputes.filter(d => d.priority === 'urgent').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Urgents
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tableau des litiges */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Litige</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Priorité</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Montant</TableCell>
              <TableCell>Assigné à</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDisputes.map((dispute) => (
              <TableRow key={dispute.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar 
                      src={dispute.dressImage} 
                      variant="rounded"
                      sx={{ mr: 2, width: 40, height: 40 }}
                    />
                    <Box>
                      <Typography variant="subtitle2">{dispute.dressTitle}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {dispute.complainant.name} vs {dispute.respondent.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {dispute.createdAt.toLocaleDateString('fr-FR')}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getTypeIcon(dispute.type)}
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      {dispute.type}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={dispute.priority} 
                    color={getPriorityColor(dispute.priority) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={dispute.status} 
                    color={getStatusColor(dispute.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {dispute.amount}€
                  </Typography>
                </TableCell>
                <TableCell>
                  {dispute.assignedTo ? (
                    <Typography variant="body2">
                      {dispute.assignedTo.name}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Non assigné
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Voir détails">
                      <IconButton size="small" onClick={() => {
                        setSelectedDispute(dispute);
                        setOpenDetailsDialog(true);
                      }}>
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    {dispute.status === 'open' && (
                      <Tooltip title="Assigner">
                        <IconButton size="small" onClick={() => handleDisputeAction(dispute.id, 'assign')}>
                          <Assignment />
                        </IconButton>
                      </Tooltip>
                    )}
                    {dispute.status === 'investigating' && (
                      <Tooltip title="Résoudre">
                        <IconButton size="small" onClick={() => {
                          setSelectedDispute(dispute);
                          setOpenResolutionDialog(true);
                        }}>
                          <CheckCircle color="success" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {dispute.priority !== 'urgent' && (
                      <Tooltip title="Escalader">
                        <IconButton size="small" onClick={() => handleDisputeAction(dispute.id, 'escalate')}>
                          <Flag color="error" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de détails du litige */}
      <Dialog open={openDetailsDialog} onClose={() => setOpenDetailsDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              Détails du litige - {selectedDispute?.dressTitle}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip 
                label={selectedDispute?.priority} 
                color={getPriorityColor(selectedDispute?.priority || '') as any}
                size="small"
              />
              <Chip 
                label={selectedDispute?.status} 
                color={getStatusColor(selectedDispute?.status || '') as any}
                size="small"
              />
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedDispute && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Informations du litige
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Type" 
                        secondary={selectedDispute.type}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Montant" 
                        secondary={`${selectedDispute.amount}€`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Date de création" 
                        secondary={selectedDispute.createdAt.toLocaleString('fr-FR')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Dernière mise à jour" 
                        secondary={selectedDispute.updatedAt.toLocaleString('fr-FR')}
                      />
                    </ListItem>
                  </List>

                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Parties impliquées
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ position: 'relative', mr: 1 }}>
                              <Avatar src={selectedDispute.complainant.avatar} />
                              <VerifiedBadge 
                                isVerified={selectedDispute.complainant.isVerified} 
                                position="avatar" 
                                size="small"
                              />
                            </Box>
                            <Typography variant="subtitle2">Plaignant</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="body2">{selectedDispute.complainant.name}</Typography>
                            <VerifiedBadge 
                              isVerified={selectedDispute.complainant.isVerified} 
                              position="inline" 
                              size="small"
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {selectedDispute.complainant.email}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ position: 'relative', mr: 1 }}>
                              <Avatar src={selectedDispute.respondent.avatar} />
                              <VerifiedBadge 
                                isVerified={selectedDispute.respondent.isVerified} 
                                position="avatar" 
                                size="small"
                              />
                            </Box>
                            <Typography variant="subtitle2">Défendeur</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="body2">{selectedDispute.respondent.name}</Typography>
                            <VerifiedBadge 
                              isVerified={selectedDispute.respondent.isVerified} 
                              position="inline" 
                              size="small"
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {selectedDispute.respondent.email}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Description
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3 }}>
                    {selectedDispute.description}
                  </Typography>

                  <Typography variant="h6" gutterBottom>
                    Preuves ({selectedDispute.evidence.length})
                  </Typography>
                  <List>
                    {selectedDispute.evidence.map((evidence) => (
                      <ListItem key={evidence.id}>
                        <ListItemIcon>
                          {evidence.type === 'photo' ? <PhotoCamera /> :
                           evidence.type === 'message' ? <Message /> :
                           evidence.type === 'document' ? <DocumentIcon /> :
                           <Description />}
                        </ListItemIcon>
                        <ListItemText
                          primary={evidence.description}
                          secondary={`Ajouté par ${evidence.uploadedBy} • ${evidence.timestamp.toLocaleString('fr-FR')}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Chronologie
                  </Typography>
                  <List>
                    {selectedDispute.timeline.map((event, index) => (
                      <React.Fragment key={event.id}>
                        <ListItem alignItems="flex-start">
                          <ListItemIcon>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                backgroundColor: getStatusColor(event.type) === 'error' ? '#f44336' :
                                               getStatusColor(event.type) === 'warning' ? '#ff9800' :
                                               getStatusColor(event.type) === 'success' ? '#4caf50' : '#2196f3',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white'
                              }}
                            >
                              {event.type === 'status_change' ? <Assignment /> :
                               event.type === 'evidence_added' ? <PhotoCamera /> :
                               event.type === 'comment' ? <Message /> :
                               <CheckCircle />}
                            </Box>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box>
                                <Typography variant="subtitle2">{event.action}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {event.description}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {event.user} • {event.timestamp.toLocaleString('fr-FR')}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < selectedDispute.timeline.length - 1 && (
                          <Divider variant="inset" component="li" />
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                </Grid>

                {selectedDispute.resolution && (
                  <Grid item xs={12}>
                    <Alert severity="success">
                      <Typography variant="h6" gutterBottom>
                        Résolution
                      </Typography>
                      <Typography variant="body2">
                        <strong>Décision:</strong> {getResolutionDecisionText(selectedDispute.resolution.decision)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Montant:</strong> {selectedDispute.resolution.amount}€
                      </Typography>
                      <Typography variant="body2">
                        <strong>Description:</strong> {selectedDispute.resolution.description}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Résolu par:</strong> {selectedDispute.resolution.resolvedBy} • {selectedDispute.resolution.resolvedAt.toLocaleString('fr-FR')}
                      </Typography>
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailsDialog(false)}>Fermer</Button>
          {selectedDispute && selectedDispute.status === 'open' && (
            <Button 
              color="primary" 
              onClick={() => {
                handleDisputeAction(selectedDispute.id, 'assign');
                setOpenDetailsDialog(false);
              }}
            >
              Assigner
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog de résolution */}
      <Dialog open={openResolutionDialog} onClose={() => setOpenResolutionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Résoudre le litige
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Décision</InputLabel>
              <Select
                value={resolutionData.decision}
                label="Décision"
                onChange={(e) => setResolutionData({ ...resolutionData, decision: e.target.value })}
              >
                <MenuItem value="favor_complainant">En faveur du plaignant</MenuItem>
                <MenuItem value="favor_respondent">En faveur du défendeur</MenuItem>
                <MenuItem value="partial_refund">Remboursement partiel</MenuItem>
                <MenuItem value="no_action">Aucune action</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Montant (€)"
              type="number"
              value={resolutionData.amount}
              onChange={(e) => setResolutionData({ ...resolutionData, amount: Number(e.target.value) })}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Description de la résolution"
              multiline
              rows={4}
              value={resolutionData.description}
              onChange={(e) => setResolutionData({ ...resolutionData, description: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenResolutionDialog(false)}>Annuler</Button>
          <Button 
            color="success" 
            onClick={() => {
              handleDisputeAction(selectedDispute?.id || '', 'resolve', resolutionData);
              setOpenResolutionDialog(false);
              setResolutionData({ decision: '', amount: 0, description: '' });
            }}
            disabled={!resolutionData.decision || !resolutionData.description}
          >
            Résoudre
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DisputeManagement;
